// Developer: Ankit Gupta
/**
 * What kind of connection a project environment is.
 *
 * Oracle Primavera P6 is read by Insight's schedule analytics through P6's own
 * web services. It is not a database and not a Fusion pod, so no screen may
 * offer it as a SQL source, a sync source or a migration source. The server
 * refuses those uses too; filtering here means nobody is offered a choice
 * that can only fail.
 */
import i18n from '@/i18n';

export const PRIMAVERA = 'PRIMAVERA';
export const PRIMAVERA_LABEL = 'Oracle Primavera P6';
/** Read when shown, so it is in the reader's language. */
export const PRIMAVERA_USAGE_EN = 'Primavera: used by Insight schedule analytics';
export const primaveraUsage = (): string =>
  i18n.t('common:primaveraUsage', { defaultValue: PRIMAVERA_USAGE_EN });

/**
 * Takes any environment-shaped object. Some screens' environment types carry
 * no instance_type at all; for those this is simply false.
 */
export function isPrimaveraEnv(env: object | null | undefined): boolean {
  const value = env ? (env as { instance_type?: unknown }).instance_type : undefined;
  return typeof value === 'string' && value.trim().toUpperCase() === PRIMAVERA;
}

/**
 * A REST API connection (ManageEngine ServiceDesk Plus and the like): a base
 * URL and a key that Orion's assistants call through the session gateway.
 * Like P6 it is never a SQL, sync or migration source.
 */
export const REST_API = 'REST_API';

export function isRestApiEnv(env: object | null | undefined): boolean {
  const value = env ? (env as { instance_type?: unknown }).instance_type : undefined;
  return typeof value === 'string' && value.trim().toUpperCase() === REST_API;
}

/**
 * An Oracle Analytics instance: read by Orion's assistants through the session
 * gateway, with each person's own token. Never a SQL, sync or migration source.
 */
export const ORACLE_ANALYTICS = 'ORACLE_ANALYTICS';

export function isOacEnv(env: object | null | undefined): boolean {
  const value = env ? (env as { instance_type?: unknown }).instance_type : undefined;
  return typeof value === 'string' && value.trim().toUpperCase() === ORACLE_ANALYTICS;
}

/** P6, a REST API or Oracle Analytics: read through web services, never queried with SQL. */
export function isNotSqlEnv(env: object | null | undefined): boolean {
  return isPrimaveraEnv(env) || isRestApiEnv(env) || isOacEnv(env);
}

/** The environments a SQL, sync or migration picker may offer (no P6, no REST API). */
export function withoutPrimavera<T extends object>(envs: T[]): T[] {
  return envs.filter((env) => !isNotSqlEnv(env));
}
